/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { processImage } from 'js/lib/imgix';

import { breakpoints } from '@coursera/cds-core';
import { SvgPlayFilled } from '@coursera/coursera-ui/svg';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { Maybe } from 'bundles/premium-hub/__generated__/baseContentfulTypes';
import type {
  PremiumHubAudioDescriptionAssetFragment,
  PremiumHubImageAssetFragment,
  PremiumHubVideoAssetFragment,
} from 'bundles/premium-hub/__generated__/contentfulTypes';
import VideoControlBar from 'bundles/premium-hub/components/shared/VideoControlBar';

import _t from 'i18n!nls/premium-hub';

type Props = {
  videoFile: PremiumHubVideoAssetFragment;
  audioDescriptionTrack?: Maybe<PremiumHubAudioDescriptionAssetFragment>;
  placeholderImage: PremiumHubImageAssetFragment;
  index: number;
  authorName?: string | null;
  authorTitle?: string | null;
  setVideoElementRef: (element: HTMLVideoElement | null) => void;
  playVideoElement: () => void;
  text?: string | null;
  slideNumber: number;
};

const styles = {
  videoContainer: css`
    position: relative;
    border-radius: 8px 8px 0 0;
    background-color: var(--cds-color-neutral-primary);
    aspect-ratio: 16 / 9;
    overflow: clip;
    overflow-clip-margin: 4px;

    ${breakpoints.up('md')} {
      border-radius: 8px 0 0 8px;
      aspect-ratio: unset;
      height: 434px;
    }

    &:fullscreen {
      width: 100%;
      height: 100%;
      border-radius: 0;
      overflow: visible;
    }
  `,

  video: css`
    position: absolute;
    width: 100%;
    height: 100%;
    margin: auto;
    cursor: pointer;
  `,

  videoOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--cds-color-neutral-primary);
      opacity: 0.5;
    }

    &:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 3px var(--cds-color-white-0);
    }
  `,

  playIcon: css`
    border: 3px solid var(--cds-color-white-0);
    border-radius: calc(var(--cds-border-radius-400) + var(--cds-border-radius-50));
    width: 66px;
    height: 66px;
    padding: calc(var(--cds-spacing-150) / 2 + var(--cds-spacing-100));
    margin: 0 auto;
    z-index: 1;
    background-color: var(--cds-color-darken-400);

    :hover,
    :active,
    :focus {
      background-color: var(--cds-color-darken-400);
    }
  `,
};

const TestimonialVideo = ({
  videoFile,
  setVideoElementRef,
  playVideoElement,
  audioDescriptionTrack,
  placeholderImage,
  index,
  authorName,
  authorTitle,
  text,
  slideNumber,
}: Props) => {
  const [videoPlayed, setVideoPlayed] = useState(false);
  const trackV3 = useTracker();
  const localVideoRef = useRef<HTMLVideoElement | null>(null);

  if (!videoFile.url || !placeholderImage) {
    return null;
  }

  const { url, width, height } = videoFile;
  const hasTextTrack = !!(audioDescriptionTrack && audioDescriptionTrack.url);

  const compressedImageUrl = processImage(placeholderImage.url, {
    auto: 'format,compress',
    width: width || 640,
    height: height || 360,
    quality: 60,
  });

  const testimonialCardTrackingData = {
    author: authorName ?? '',
    text: text ?? '',
    index: index ?? -1,
  };

  const handlePlayVideo = () => {
    playVideoElement?.();
    setVideoPlayed(true);
    trackV3('click_testimonial_card', {
      testimonialCard: testimonialCardTrackingData,
      testimonialAction: 'play_video',
    });
  };

  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const video = localVideoRef.current;
    if (!video || !videoPlayed) return;
    // Only intercept when focus is directly on the video element;
    // the toolbar has its own keyboard handler for its controls
    const target = e.target as HTMLElement;
    if (target !== video) return;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        e.stopPropagation();
        video.currentTime = Math.min(video.duration || 0, video.currentTime + 5);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        e.stopPropagation();
        video.currentTime = Math.max(0, video.currentTime - 5);
        break;
      case ' ':
      case 'k':
        e.preventDefault();
        e.stopPropagation();
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
        break;
      default:
        break;
    }
  };

  return (
    <div css={styles.videoContainer} onKeyDown={handleContainerKeyDown}>
      <video
        crossOrigin="anonymous"
        tabIndex={-1}
        ref={(element) => {
          localVideoRef.current = element;
          setVideoElementRef(element);
        }}
        css={styles.video}
        src={url}
        onClick={() => {
          const video = localVideoRef.current;
          if (video && videoPlayed) {
            if (video.paused) {
              video.play();
            } else {
              video.pause();
            }
          }
        }}
        onPause={() =>
          trackV3('click_testimonial_card', {
            testimonialCard: testimonialCardTrackingData,
            testimonialAction: 'pause_video',
          })
        }
      >
        {hasTextTrack && (
          <track label="Audio description" kind="descriptions" srcLang="en" src={audioDescriptionTrack!.url!} />
        )}
      </video>

      {!videoPlayed && (
        <TrackedDiv
          trackingName="testimonial"
          data={{ indexPosition: index + 1, testimonialName: authorName ?? '', testimonialTitle: authorTitle ?? '' }}
          withVisibilityTracking
          onClick={handlePlayVideo}
          role="button"
          aria-label={_t('Slide #{slideNumber}. Play testimonial video, #{description}', {
            description: placeholderImage.description,
            slideNumber,
          })}
          css={[
            styles.videoOverlay,
            css`
              background-image: url(${compressedImageUrl});
            `,
          ]}
          tabIndex={0}
        >
          <div css={styles.playIcon}>
            <SvgPlayFilled size={36} color="var(--cds-color-white-0)" />
          </div>
        </TrackedDiv>
      )}

      {videoPlayed && <VideoControlBar videoRef={localVideoRef} hasTextTrack={hasTextTrack} />}
    </div>
  );
};
export default TestimonialVideo;
